<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="Society List"
      subtitle="Manage all registered societies on the platform."
      :headers="headers"
      :items="societies"
      :loading="loading"
      show-search
      :search.sync="searchText"
      search-placeholder="Search societies..."
      show-add
      add-label="Add Society"
      @add="openAdd"
    >
      <template v-slot:item.type="{ item }">
        <span class="ism-badge" :class="item.type == 1 ? 'ism-badge-blue' : (item.type == 2 ? 'ism-badge-accent' : 'ism-badge-purple')">
          {{ item.type == 1 ? 'Residential' : (item.type == 2 ? 'Commercial' : 'Both') }}
        </span>
      </template>

      <!-- Name with Logo -->
      <template v-slot:item.name="{ item }">
        <div style="display:flex;align-items:center;gap:12px;">
          <PremiumAvatar
            :image="item.logo_url ? $resolveUploadUrl(item.logo_url) : null"
            :name="item.name"
            :size="32"
            variant="squircle"
          />
          <span style="font-weight:600;font-size:13.5px;color:var(--text-primary);">{{ item.name }}</span>
        </div>
      </template>

      <!-- Address -->
      <template v-slot:item.address="{ item }">
        <TruncatedCell
          :text="`${item.address}, ${item.cityData ? item.cityData.name : (item.other_city || item.city)}, ${item.stateData ? item.stateData.name : (item.other_state || item.state)}`"
          max-width="200px"
        />
      </template>

      <!-- Contact number -->
      <template v-slot:item.contact_person_number="{ item }">
        <span style="white-space:nowrap;font-size:13.5px;">
          {{ item.country_code ? (item.country_code.startsWith('+') ? item.country_code : '+' + item.country_code) : '' }} {{ item.contact_person_number }}
        </span>
      </template>

      <!-- Status Toggle -->
      <template v-slot:item.is_active="{ item }">
        <v-switch
          v-model="item.is_active"
          color="#F26522"
          hide-details
          dense
          class="mt-0 pt-0"
          @change="changeStatus(item.id)"
        />
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div style="display:flex;gap:8px;align-items:center;">
          <PremiumActionButton
            :to="'/superadmin/society/list/' + item.id + '/details'"
            icon="mdi-eye-outline"
            label="View"
            variant="info"
          />
          <PremiumActionButton
            icon="mdi-pencil-outline"
            label="Edit"
            @click="openEdit(item)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- Add/Edit Modal -->
    <PremiumModalForm
      v-model="societyDialog"
      :title="isEdit ? 'Edit Society' : 'Add Society'"
      :submitText="isEdit ? 'Update Society' : 'Create Society'"
      :loading="saving"
      max-width="600"
      @submit="saveSociety"
    >
      <div style="display:flex;flex-direction:column;gap:16px;">
        <!-- Type Selection -->
        <div class="ism-field-group">
          <label class="ism-field-label">Society Type *</label>
          <v-radio-group v-model="formData.societyType" row class="mt-0 pt-0" hide-details>
            <v-radio label="Residential" :value="1" color="#F26522"></v-radio>
            <v-radio label="Commercial" :value="2" color="#F26522"></v-radio>
            <v-radio label="Both" :value="3" color="#F26522"></v-radio>
          </v-radio-group>
        </div>
        <v-checkbox
          v-model="formData.has_multiple_building"
          label="Has multiple building"
          :true-value="1"
          :false-value="0"
          hide-details
          class="mt-0"
          color="#F26522"
        ></v-checkbox>

        <v-row dense>
          <v-col v-if="formData.societyType != 2" cols="6">
            <PremiumInput
              v-model="formData.noOfHouse"
              label="No. of Houses *"
              name="noOfHouse"
              placeholder="0"
              type="number"
              v-validate="'required|numeric'"
              :error="errors.first('noOfHouse') || serverSideErrors.noOfHouse"
            />
          </v-col>
          <v-col v-if="formData.societyType != 1" :cols="formData.societyType == 2 ? 12 : 6">
            <PremiumInput
              v-model="formData.noOfShop"
              label="No. of Shops *"
              name="noOfShop"
              placeholder="0"
              type="number"
              v-validate="'required|numeric'"
              :error="errors.first('noOfShop') || serverSideErrors.noOfShop"
            />
          </v-col>
        </v-row>

        <PremiumInput
          v-model="formData.name"
          label="Society Name *"
          name="name"
          placeholder="Enter society name"
          v-validate="'required'"
          :error="errors.first('name') || serverSideErrors.name"
        />

        <PremiumInput
          v-model="formData.address"
          label="Address *"
          name="address"
          placeholder="Enter full address"
          is-textarea
          rows="2"
          v-validate="'required'"
          :error="errors.first('address') || serverSideErrors.address"
        />

        <v-row dense>
          <v-col cols="12">
            <PremiumInput
              v-model="formData.country"
              label="Country *"
              name="country"
              placeholder="Select country"
              is-select
              :items="countries"
              item-text="name"
              item-value="id"
              v-validate="'required'"
              :error="errors.first('country') || serverSideErrors.country"
              @change="onCountryChange"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="6">
            <PremiumInput
              v-model="formData.state"
              label="State *"
              name="state"
              placeholder="Select state"
              is-select
              :items="states"
              item-text="name"
              item-value="id"
              :disabled="!formData.country"
              v-validate="'required'"
              :error="errors.first('state') || serverSideErrors.state"
              @input="onStateChange"
            />
          </v-col>
          <v-col v-if="formData.state === 'Other' || formData.state === 'other'" cols="6">
            <PremiumInput
              key="other-state-input"
              v-model="formData.otherState"
              label="Specify State *"
              name="otherState"
              data-vv-name="otherState"
              data-vv-as="state"
              placeholder="Enter your state"
              v-validate="'required'"
              :error="errors.first('otherState') || serverSideErrors.otherState"
            />
          </v-col>

          <v-col cols="6">
            <PremiumInput
              v-model="formData.city"
              label="City *"
              name="city"
              :placeholder="(formData.state === 'Other' || formData.state === 'other') ? 'Enter city manually below' : 'Select city'"
              is-select
              :items="cities"
              item-text="name"
              item-value="id"
              :disabled="!formData.state || formData.state === 'Other' || formData.state === 'other'"
              v-validate="'required'"
              :error="errors.first('city') || serverSideErrors.city"
            />
          </v-col>
          <v-col v-if="formData.city === 'Other' || formData.city === 'other'" cols="6">
            <PremiumInput
              key="other-city-input"
              v-model="formData.otherCity"
              label="Specify City *"
              name="otherCity"
              data-vv-name="otherCity"
              data-vv-as="city"
              placeholder="Enter your city"
              v-validate="'required'"
              :error="errors.first('otherCity') || serverSideErrors.otherCity"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12">
            <PremiumInput
              v-model="formData.pincode"
              label="Pincode *"
              name="pincode"
              placeholder="Pincode"
              v-validate="'required'"
              :error="errors.first('pincode') || serverSideErrors.pincode"
            />
          </v-col>
        </v-row>

        <!-- Logo Upload -->
        <div class="ism-field-group">
          <label class="ism-field-label">Society Logo {{ isEdit ? '' : '*' }}</label>
          <div class="ism-file-upload">
            <input type="file" ref="logo_input" accept="image/*" @change="onLogoSelected" hidden>
            <div class="ism-file-preview" @click="$refs.logo_input.click()">
              <img v-if="logoPreview || (isEdit && formData.logo_url)" :src="logoPreview || $resolveUploadUrl(formData.logo_url)" alt="Logo">
              <v-icon v-else size="32" color="#cbd5e1">mdi-camera-outline</v-icon>
            </div>
            <div class="ism-file-info">
              <span class="file-name">{{ selectedLogoName || 'Click to upload logo' }}</span>
              <span class="file-hint">JPG, PNG, SVG up to 2MB</span>
            </div>
          </div>
        </div>

        <PremiumInput
          v-model="formData.contactPersonName"
          label="Contact Person Name *"
          name="contactPersonName"
          placeholder="Full Name"
          v-validate="'required'"
          :error="errors.first('contactPersonName') || serverSideErrors.contactPersonName"
        />

        <div class="ism-mobile-field">
          <label class="premium-label">Mobile Number *</label>
          <div class="ism-mobile-row" :class="{ 'has-mobile-error': errors.has('contactPersonNumber') }">
            <div class="ism-mobile-code">
              <div class="ism-country-selector-wrap">
                <country-code-select
                  v-model="formData.countryIso"
                  @onSelect="onCountrySelect"
                />
              </div>
            </div>
            <div class="ism-mobile-number">
              <PremiumInput
                v-model="formData.contactPersonNumber"
                name="contactPersonNumber"
                placeholder="e.g. 9876543210"
                v-validate="'required|numeric|min:8'"
                :error="errors.first('contactPersonNumber') || serverSideErrors.contactPersonNumber"
                data-vv-as="Mobile Number"
              />
            </div>
          </div>
        </div>

        <PremiumInput
          v-model="formData.contactPersonEmail"
          label="Email Address *"
          name="contactPersonEmail"
          placeholder="admin@example.com"
          type="email"
          v-validate="'required|email'"
          :error="errors.first('contactPersonEmail') || serverSideErrors.contactPersonEmail"
        />
      </div>
    </PremiumModalForm>

    <!-- Logo Cropper -->
    <ImageCropper
      v-model="cropperOpen"
      :image-src="rawLogoSrc"
      title="Crop Society Logo"
      shape="square"
      :output-size="400"
      @cropped="onLogoCropped"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { useFilterStore } from '@/pinia/filterStore';
import PremiumTable from '../../common/PremiumTable.vue';
import PremiumActionButton from '../../common/PremiumActionButton.vue';
import PremiumModalForm from '../../common/PremiumModalForm.vue';
import PremiumInput from '../../common/PremiumInput.vue';
import PremiumAvatar from '../../common/PremiumAvatar.vue';
import TruncatedCell from '../../common/TruncatedCell.vue';
import ImageCropper from '../../common/ImageCropper.vue';

export default {
  name: 'SocietyList',
  components: { PremiumTable, PremiumActionButton, PremiumModalForm, PremiumInput, TruncatedCell, ImageCropper, PremiumAvatar },

  data() {
    return {
      societies: [],
      loading:   false,
      saving:    false,
      societyDialog: false,
      isEdit:    false,
      editId:    null,
      logoPreview: null,
      searchText: '',
      API_BASE_URL: process.env.VUE_APP_API_URL || 'https://api-staging.ismartsociety.com/api',
      selectedLogoName: '',
      cropperOpen: false,
      rawLogoSrc: '',
      formData: {
        societyType: 1,
        noOfHouse: 0,
        noOfShop: 0,
        has_multiple_building: 0,
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        contactPersonName: '',
        contactPersonNumber: '',
        contactPersonEmail: '',
        countryCode: '',
        countryIso: '',
        logo: null,
        otherState: '',
        otherCity: ''
      },
      countries: [],
      states: [],
      cities: [],
      serverSideErrors: {},
      headers: [
        { text: 'Name',           value: 'name',                  width: '20%' },
        { text: 'Code',           value: 'society_code',          width: '9%' },
        { text: 'Type',           value: 'type',                  width: '11%' },
        { text: 'Address',        value: 'address',               width: '20%' },
        { text: 'Contact Person', value: 'contact_person_name',   width: '13%' },
        { text: 'Contact No.',    value: 'contact_person_number', width: '18%', sortable: false },
        { text: 'Status',         value: 'is_active',             width: '8%',  sortable: false },
        { text: 'Actions',        value: 'actions',               width: '8%',  sortable: false },
      ],
    };
  },

  computed: {
    filterStore() { return useFilterStore(); }
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    resetFormData() {
      return {
        societyType: 1,
        noOfHouse: 0,
        noOfShop: 0,
        has_multiple_building: 0,
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        contactPersonName: '',
        contactPersonNumber: '',
        contactPersonEmail: '',
        countryCode: '',
        countryIso: '',
        logo: null,
        otherState: '',
        otherCity: ''
      };
    },

    getSocieties() {
      this.loading = true;
      this.$axios.get('superadmin/society').then(res => {
        this.loading = false;
        const data = res.data;
        if (data.status) {
          this.societies = data.socities || data.societies || data.data || [];
        } else if (Array.isArray(data)) {
          this.societies = data;
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message || 'Failed to load societies' });
        }
      }).catch(() => { this.loading = false; });
    },

    getCountries() {
      return this.$axios.get('society/countries').then(res => {
        if (res.data.status) {
          this.countries = res.data.data.map(item => ({ ...item, id: item.id || item._id }));
        }
      }).catch(() => {});
    },

    getStates(country_id) {
      this.states = [];
      this.cities = [];
      if (!country_id) return Promise.resolve();
      return this.$axios.get('society/states', { params: { country_id } }).then(res => {
        if (res.data.status) {
          this.states = res.data.data.map(item => ({ ...item, id: item.id || item._id }));
          this.states.push({ id: 'Other', name: 'Other' });
        }
      }).catch(() => {
        this.states = [];
        this.cities = [];
      });
    },

    getCities(state_id) {
      this.cities = [];
      if (!state_id || state_id === 'Other' || state_id === 'other') {
        this.cities = [{ id: 'Other', name: 'Other' }];
        return Promise.resolve();
      }
      
      return this.$axios.get('society/cities', { params: { state_id } }).then(res => {
        if (res.data.status) {
          this.cities = res.data.data.map(item => ({ ...item, id: item.id || item._id }));
          this.cities.push({ id: 'Other', name: 'Other' });
        }
      }).catch(() => {
        this.cities = [];
      });
    },

    onCountryChange(val) {
      this.formData.state = '';
      this.formData.otherState = '';
      this.formData.city = '';
      this.formData.otherCity = '';
      this.getStates(val);
    },

    onStateChange(val) {
      if (val === 'Other' || val === 'other') {
        this.formData.city = 'Other';
      } else {
        this.formData.city = '';
        this.formData.otherState = '';
      }
      this.formData.otherCity = '';
      this.getCities(val);
    },

    changeStatus(id) {
      this.$axios.post('superadmin/society/' + id + '/changeStatus').then(res => {
        const data = res.data;
        const variant = data.status ? 'green accent-4' : 'red accent-3';
        this.setAlert({ showAlert: true, variant, timeout: 3000, message: data.message });
        if (!data.status) this.getSocieties();
      });
    },

    openAdd() {
      this.isEdit = false;
      this.formData = {
        societyType: 1, noOfHouse: 0, noOfShop: 0, name: '', address: '', city: '', state: '', 
        country: '', pincode: '', contactPersonName: '', contactPersonNumber: '', contactPersonEmail: '',
        countryCode: '', countryIso: '', logo: null, has_multiple_building: 0, otherState: '', otherCity: ''
      };
      this.states = [];
      this.cities = [];
      this.logoPreview = null;
      this.selectedLogoName = '';
      this.serverSideErrors = {};
      this.$validator.reset();
      this.societyDialog = true;
    },

    openEdit(item) {
      this.isEdit = true;
      this.editId = item.id;
      this.serverSideErrors = {};
      this.logoPreview = null;
      this.selectedLogoName = '';
      this.$validator.reset();

      const countryId = item.countryData?._id || item.countryData?.id || item.country || item.country_id || '';
      const stateId = item.stateData?._id || item.stateData?.id || item.state || item.state_id || '';
      const cityId = item.cityData?._id || item.cityData?.id || item.city || item.city_id || '';
      const otherState = item.other_state || item.otherState || '';
      const otherCity = item.other_city || item.otherCity || '';

      this.formData = {
        societyType: item.type || 1,
        noOfHouse: item.no_of_house || 0,
        noOfShop: item.no_of_shop || 0,
        has_multiple_building: item.has_multiple_building || 0,
        name: item.name || '',
        address: item.address || '',
        city: cityId || '',
        state: stateId || '',
        country: countryId || '',
        pincode: item.pincode || '',
        contactPersonName: item.contact_person_name || '',
        contactPersonNumber: item.contact_person_number || '',
        contactPersonEmail: item.contact_person_email || '',
        countryCode: item.countryCode || item.country_code || '',
        countryIso: item.countryIso || '',
        logo: null,
        logo_url: item.logo_url || item.logoUrl || '',
        otherState: otherState,
        otherCity: otherCity
      };

      const loadLocation = async () => {
        // If state is null/empty but other_state exists → pre-select "Other" for state
        const isOtherState = !stateId && otherState;
        // If city is null/empty but other_city exists → pre-select "Other" for city
        const isOtherCity  = !cityId  && otherCity;

        if (!countryId) {
          if (stateId === 'Other' || stateId === 'other' || isOtherState) {
            this.formData.state = 'Other';
            this.formData.otherState = otherState;
            this.cities = [{ id: 'Other', name: 'Other' }];
            this.formData.city = 'Other';
            this.formData.otherCity = otherCity;
          }
          return;
        }

        await this.getStates(countryId);

        if (stateId === 'Other' || stateId === 'other' || isOtherState) {
          // State is "Other" — pre-select Other and fill otherState
          this.formData.state = 'Other';
          this.formData.otherState = otherState;
          this.cities = [{ id: 'Other', name: 'Other' }];
          this.formData.city = 'Other';
          this.formData.otherCity = otherCity;
        } else if (stateId) {
          this.formData.state = stateId;
          await this.getCities(stateId);

          if (cityId && cityId !== 'Other' && cityId !== 'other') {
            this.formData.city = cityId;
          } else if (isOtherCity || cityId === 'Other' || cityId === 'other') {
            // City is "Other" — pre-select Other and fill otherCity
            this.formData.city = 'Other';
            this.formData.otherCity = otherCity;
            if (!this.cities.find(c => c.id === 'Other')) {
              this.cities.push({ id: 'Other', name: 'Other' });
            }
          }
        }
      };

      loadLocation().finally(() => {
        this.societyDialog = true;
      });
    },

    onLogoSelected(e) {
      const file = e.target.files[0];
      if (!file) return;
      e.target.value = '';
      const reader = new FileReader();
      reader.onload = ev => {
        this.rawLogoSrc = ev.target.result;
        this.cropperOpen = true;
      };
      reader.readAsDataURL(file);
    },

    onLogoCropped({ file, previewUrl }) {
      this.formData.logo   = file;
      this.logoPreview     = previewUrl;
      this.selectedLogoName = 'cropped-logo.jpg';
    },

    onCountrySelect(e) {
      this.formData.countryCode = '+' + e.dialCode;
      this.formData.countryIso = e.iso2;
    },

    saveSociety() {
      this.$validator.validateAll().then(valid => {
        if (!valid) return;
        this.saving = true;
        
        let fd = new FormData();
        Object.keys(this.formData).forEach(key => {
          let val = this.formData[key];
          if (val !== null) fd.append(key, val);
        });

        // Map manual fields to database columns for compatibility
        if (this.formData.state === 'Other' || this.formData.state === 'other') {
          fd.set('state', '0'); // Send 0 or specific flag to backend for ID column
          fd.set('other_state', this.formData.otherState);
        }
        if (this.formData.city === 'Other' || this.formData.city === 'other') {
          fd.set('city', '0');
          fd.set('other_city', this.formData.otherCity);
        }

        const url    = this.isEdit ? `superadmin/society/${this.editId}` : 'superadmin/society';
        const method = this.isEdit ? 'put' : 'post';
        
        this.$axios[method](url, fd).then(res => {
          this.saving = false;
          if (res.data.status) {
            this.societyDialog = false;
            this.getSocieties();
            this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: res.data.message });
          } else {
            if (res.data.errors) {
              res.data.errors.forEach(err => { this.serverSideErrors[err.field] = err.message; });
            } else {
              this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
            }
          }
        }).catch(() => { this.saving = false; });
      });
    },
  },

  mounted() {
    this.$validator.localize('en', {
      custom: {
        contactPersonNumber: {
          required: 'Mobile number is required',
          numeric: 'Please enter a valid mobile number',
          min: 'Mobile number must be at least 8 digits'
        },
        name: { required: 'Society name is required' },
        address: { required: 'Society address is required' },
        city: { required: 'City is required' },
        state: { required: 'State is required' },
        pincode: { required: 'Pincode is required' },
        contactPersonName: { required: 'Contact person name is required' },
        otherState: { required: 'Please specify the state' },
        otherCity: { required: 'Please specify the city' }
      }
    });
    this.getSocieties();
    this.getCountries();

    // ── Restore Filters ──
    // Trigger module switch check (clears other module filters if we just arrived here)
    this.filterStore.updateFilter('society_list', {});
    const saved = this.filterStore.getFilter('society_list');
    if (saved && saved.search) {
      this.searchText = saved.search;
    }
  },

  watch: {
    searchText(val) {
      this.filterStore.updateFilter('society_list', { search: val });
    }
  }
};
</script>

<style scoped>
/* Modal Field Styles */
.ism-field-group { display: flex; flex-direction: column; gap: 4px; }
.ism-field-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-secondary); margin-bottom: 4px; }

/* File Upload */
.ism-file-upload { display: flex; align-items: center; gap: 16px; padding: 12px; background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: 12px; }
.ism-file-preview { width: 64px; height: 64px; border-radius: 10px; background: #f8fafc; border: 2px dashed #cbd5e1; display: flex; align-items: center; justify-content: center; overflow: hidden; cursor: pointer; flex-shrink: 0; transition: all 0.2s; }
.ism-file-preview:hover { border-color: #F26522; background: rgba(242, 101, 34, 0.04); }
.ism-file-preview img { width: 100%; height: 100%; object-fit: contain; }
.ism-file-info { display: flex; flex-direction: column; gap: 2px; }
.file-name { font-size: 13px; font-weight: 600; color: var(--text-primary); max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-hint { font-size: 11px; color: var(--text-secondary); }

/* Mobile Row Alignment */
.ism-mobile-field { margin-bottom: 20px; }
.ism-mobile-row { display: flex; gap: 12px; align-items: flex-start; }
.ism-mobile-code { width: 80px; }
.ism-mobile-number { flex: 1; }

/* Sync borders on error */
.has-mobile-error .ism-country-selector-wrap {
  border-color: #ef4444 !important;
}
.has-mobile-error .ism-mobile-number >>> fieldset {
  border-color: #ef4444 !important;
}
.ism-mobile-number >>> .v-text-field__details {
  margin-left: -92px !important;
  width: calc(100% + 92px) !important;
  padding-left: 0 !important;
  padding-top: 6px !important;
  display: block !important;
}
.ism-mobile-number >>> .v-messages__message {
  text-align: left !important;
}

.premium-label {
  display: block; font-size: 13px; font-weight: 600;
  color: var(--text-secondary); margin-bottom: 8px;
  font-family: "Inter", sans-serif;
  white-space: nowrap;
}

.ism-country-selector-wrap {
  height: 40px; border: 1px solid var(--border-color2);
  border-radius: 8px; background: var(--bg-primary) !important;
  display: flex; align-items: center; 
  transition: all 0.2s;
  position: relative;
  overflow: visible;
}
.ism-country-selector-wrap:hover {
  border-color: var(--accent) !important;
}

.ism-country-selector-wrap >>> .dropdown:hover {
  background-color: transparent !important;
}

/* Custom styles for the library component */
.ism-country-selector-wrap >>> .country-code-select,
.ism-country-selector-wrap >>> .vue-country-select,
.ism-country-selector-wrap >>> .selected-flag,
.ism-country-selector-wrap >>> .dropdown-button,
.ism-country-selector-wrap >>> .dropdown-toggle {
  border: none !important; 
  width: 100% !important; 
  height: 100% !important;
  background: transparent !important; 
  background-color: transparent !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 14px !important; 
  color: var(--text-primary) !important;
  box-shadow: none !important;
}

.ism-country-selector-wrap >>> .dropdown-list,
.ism-country-selector-wrap >>> ul {
  position: absolute !important;
  top: calc(100% + 4px);
  left: 0 !important;
  bottom: auto;
  background-color: var(--card-bg) !important;
  border: 1px solid var(--border-color2) !important;
  color: var(--text-primary) !important;
  z-index: 99999 !important;
  box-shadow: var(--shadow-lg) !important;
  border-radius: 8px !important;
  min-width: 250px !important;
  max-height: 250px !important;
  overflow-y: auto !important;
  padding: 6px 0 !important;
}

.ism-country-selector-wrap >>> li,
.ism-country-selector-wrap >>> .dropdown-item {
  padding: 8px 14px !important;
  background: transparent !important;
  color: var(--text-primary) !important;
  transition: all 0.2s;
}

.ism-country-selector-wrap >>> li:hover,
.ism-country-selector-wrap >>> .dropdown-item:hover {
  background-color: var(--bg-primary) !important;
  color: var(--accent) !important;
}
</style>
